import { ErrorMessage, Field } from "formik";
import PropTypes from "prop-types";
import React, { Fragment, useRef } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import ItemSearchInput from "../../components/form/ItemSearchInput";
import { formatAmount } from "../delivery_notes/utils";

const DEFAULT_AMOUNT = "1";

function WorkOrderItemsCard({ items, errors, setFieldValue }) {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);
  const amountInputs = useRef(new Map());
  const rowErrors = Array.isArray(errors) ? errors : [];
  const existingNumbers = items.map((row) => row.item.number);

  const registerAmountInput = (itemId) => (element) => {
    if (element) {
      amountInputs.current.set(itemId, element);
    } else {
      amountInputs.current.delete(itemId);
    }
  };

  const addItem = (item) => {
    const amount = formatAmount(item.default_amount) || DEFAULT_AMOUNT;
    flushSync(() => setFieldValue("items", [...items, { item, amount }]));
    const amountInput = amountInputs.current.get(item.id);
    amountInput.focus();
    amountInput.select();
  };

  const handleAmountKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    searchInputRef.current.focus();
  };

  const removeItem = (index) => {
    setFieldValue(
      "items",
      items.filter((_, i) => i !== index),
    );
  };

  return (
    <div className="card border-0 mb-4">
      <div className="card-header h6 mb-0 bg-none p-3">
        <i className="fa fa-gear fa-lg fa-fw text-dark text-opacity-50 me-1" />
        {t("work_orders.items")}
      </div>
      <div className="card-body">
        {items.length === 0 && <p className="text-muted">{t("work_orders.no_items")}</p>}
        {items.map((row, index) => {
          const amountName = `items.${index}.amount`;
          const amountClass = `form-control${rowErrors[index]?.amount ? " is-invalid" : ""}`;
          const groupName = row.item.groups?.[0]?.name;
          return (
            <Fragment key={row.item.id}>
              <div className="row align-items-center">
                <div className="col-12 col-md-3 mb-1 mb-md-0">
                  <strong>{row.item.number}</strong>
                </div>
                <div className="col-12 col-md-6 mb-1 ps-md-0">
                  {row.item.name}
                  {groupName && (
                    <span className="mx-1 badge bg-gray-200 fs-10px rounded-pill text-dark">{groupName}</span>
                  )}
                </div>
                <div className="col-12 col-md">
                  <div className="input-group">
                    <Field
                      innerRef={registerAmountInput(row.item.id)}
                      name={amountName}
                      inputMode="decimal"
                      enterKeyHint="next"
                      onKeyDown={handleAmountKeyDown}
                      className={amountClass}
                      placeholder={t("items.amount")}
                      aria-label={t("items.amount")}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      className="btn btn-danger"
                      onClick={() => removeItem(index)}
                      aria-label={t("common.remove")}
                    >
                      <i className="fa fa-minus" />
                    </button>
                  </div>
                  <ErrorMessage
                    name={amountName}
                    render={(error) => <div className="invalid-feedback d-block">{error}</div>}
                  />
                </div>
              </div>
              <hr className="mt-2 mb-2" />
            </Fragment>
          );
        })}
        <ItemSearchInput
          inputRef={searchInputRef}
          onAdd={addItem}
          existingNumbers={existingNumbers}
          placeholder={t("work_orders.search_item")}
        />
      </div>
    </div>
  );
}

WorkOrderItemsCard.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.shape({
        id: PropTypes.number.isRequired,
        number: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        groups: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
      }).isRequired,
      amount: PropTypes.string.isRequired,
    }),
  ).isRequired,
  errors: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  setFieldValue: PropTypes.func.isRequired,
};

export default WorkOrderItemsCard;
